// =============================================================================
// Pintravel — Seed the query cache from disk
// =============================================================================
// What makes the app local-first rather than merely cache-on-failure.
//
// Before this, SQLite was only consulted when a request *failed*. That covers
// aeroplane mode and nothing else — on one bar of EDGE the request does not
// fail, it hangs, and the screen sat on a skeleton for thirty seconds with the
// answer already on the phone. Which is the situation people are actually in
// while travelling.
//
// So when a session opens we push everything SQLite holds into the query cache
// first. Every screen then renders from disk on its first frame and the network
// becomes a background refresh — the thing that keeps data current, not the
// thing that decides whether anything shows at all.
// =============================================================================

import type { QueryClient } from '@tanstack/react-query';

import type { TripDetailResponse, TripListResponse } from '@/features/trips/api/trips.api';
import { HOME_TRIPS_PARAMS, tripKeys } from '@/features/trips/hooks/useTrips';
import { offlineDb } from '@/lib/db/offline-db';

/**
 * Seeded data is deliberately dated so TanStack Query treats it as stale and
 * refetches straight away. The user sees the cached trip immediately and it
 * updates underneath them; marking it fresh would have shown yesterday's plan
 * and left it there for the whole staleTime window.
 */
const SEEDED_AS_STALE = 0;

export interface HydrationResult {
  trips: number;
}

export async function hydrateQueryCacheFromOffline(
  queryClient: QueryClient,
): Promise<HydrationResult> {
  if (!offlineDb.sessionUserId) {
    return { trips: 0 };
  }

  let cached: unknown[] = [];
  try {
    cached = await offlineDb.getAllCachedTrips();
  } catch (error) {
    // A cold or corrupt database must not stop the app from starting; the
    // network path still works.
    console.warn('[Hydrate] Could not read cached trips:', error);
    return { trips: 0 };
  }

  const trips = cached.filter((trip): trip is TripDetailResponse => {
    return typeof trip === 'object' && trip !== null && 'id' in trip;
  });

  for (const trip of trips) {
    // Only seed what is not already there — a query that has run this session
    // holds fresher data than the disk.
    if (queryClient.getQueryData(tripKeys.detail(trip.id)) === undefined) {
      queryClient.setQueryData(tripKeys.detail(trip.id), trip, { updatedAt: SEEDED_AS_STALE });
    }
  }

  // The trip list too, so the home screen draws on its first frame instead of
  // showing a skeleton until the network answers or fails.
  //
  // Seeded under the key that screen actually reads. Seeding only the
  // unparameterised list — which nothing renders — meant hydration did nothing
  // for the one screen that needed it most. A filtered view still is not
  // reconstructed here: that would mean re-implementing the server's filter,
  // and guessing wrong is worse than a brief skeleton.
  if (trips.length > 0) {
    // Sorted the way the server would, so the seeded page is not merely
    // present but in the right order.
    // Trips without dates last, as the server sorts nulls.
    const time = (trip: (typeof trips)[number]) =>
      trip.startDate ? new Date(trip.startDate).getTime() : Number.POSITIVE_INFINITY;
    const sorted = [...trips].sort((a, b) => time(a) - time(b));
    const list: TripListResponse = {
      trips: sorted,
      meta: {
        page: 1,
        perPage: sorted.length,
        total: sorted.length,
        totalPages: 1,
      },
    };

    for (const key of [tripKeys.list(), tripKeys.list(HOME_TRIPS_PARAMS)]) {
      if (queryClient.getQueryData(key) === undefined) {
        queryClient.setQueryData(key, list, { updatedAt: SEEDED_AS_STALE });
      }
    }
  }

  if (trips.length > 0) {
    console.info(`[Hydrate] Seeded ${trips.length} trip(s) from disk`);
  }

  return { trips: trips.length };
}

export default hydrateQueryCacheFromOffline;
